import { subscribeToServerEvent } from "@storytell/events";
import type { StreamingChatId } from "@storytell/streaming";
import { createEffect, createRoot, on, onCleanup, onMount } from "solid-js";

const isPlaceholderTitle = (title: string) => !title || title === "New" || title === "New Chat";

// The server titles a chat's first turn with one cheap-model call started alongside the turn,
// then writes it under a 5s cap. A turn that ends before the title lands has already given that
// call its whole length, so what remains is usually its tail plus the write.
const TITLE_RETRY_MS = 5_000;

/**
 * Picks up the title the server generates for a chat. A watched chat's title is fetched each time
 * the server reports the chat changed, and once when its turn stops generating: the change event
 * only reaches routes with a project events stream, and it can be missed. Without that stream,
 * a turn-end fetch that finds no title is retried once, TITLE_RETRY_MS later. Watching ends when a
 * real title arrives or the owning scope is disposed. Leaving the chat doesn't end it: the sidebar
 * learns the title from onTitle.
 */
export const createChatTitleWatcher = (deps: {
  authToken: () => string | undefined;
  hasEventsStream: () => boolean;
  isGenerating: (chatId: StreamingChatId) => boolean;
  fetchTitle: (chatId: StreamingChatId, token: string) => Promise<string | undefined>;
  onTitle: (chatId: StreamingChatId, title: string) => void;
}) => {
  // Each watched chat's turn-end effect runs in its own root; the value disposes it.
  const watched = new Map<StreamingChatId, () => void>();

  const check = async (chatId: StreamingChatId) => {
    const token = deps.authToken();
    if (!token || !watched.has(chatId)) return;

    let title = "";
    try {
      title = (await deps.fetchTitle(chatId, token))?.trim() ?? "";
    } catch {
      return;
    }
    const stop = watched.get(chatId);
    if (!stop || isPlaceholderTitle(title)) return;
    stop();
    deps.onTitle(chatId, title);
  };

  onMount(() => {
    onCleanup(
      subscribeToServerEvent("cp.chats", (event) => {
        if (event.op === "op.update") void check(event.resource.id as StreamingChatId);
      })
    );
  });
  onCleanup(() => {
    for (const stop of watched.values()) stop();
  });

  const watch = (chatId: StreamingChatId) => {
    if (watched.has(chatId)) return;
    createRoot((dispose) => {
      watched.set(chatId, () => {
        dispose();
        watched.delete(chatId);
      });
      let retry: ReturnType<typeof setTimeout> | undefined;
      onCleanup(() => clearTimeout(retry));
      const checkThenRetry = async (): Promise<void> => {
        await check(chatId);
        if (!watched.has(chatId) || deps.hasEventsStream()) return;
        clearTimeout(retry);
        retry = setTimeout(() => void check(chatId), TITLE_RETRY_MS);
      };
      createEffect(
        on(
          () => deps.isGenerating(chatId),
          (generating, wasGenerating) => {
            if (!wasGenerating || generating) return;
            void checkThenRetry();
          }
        )
      );
    });
  };

  return { watch };
};
